import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import {
  InvitationService,
  CreateInvitationPayload,
} from '../../services/invitation.service';
import { ToastService } from '@black/ui';
import { LiveClass } from '../../core/state/live-class/models/live-class';
import { CourseItem } from '../../core/state/course/models';

@Component({
  selector: 'black-invite-student-modal',
  templateUrl: './invite-student-modal.component.html',
  styleUrls: ['./invite-student-modal.component.css'],
  standalone: false,
})
export class InviteStudentModalComponent implements OnInit, OnChanges {
  private invitationService = inject(InvitationService);
  private toast = inject(ToastService);

  @Input() isOpen = false;
  @Input() availableLiveClasses: LiveClass[] = [];
  @Input() defaultLiveClassId?: string;
  @Input() availableCourses: CourseItem[] = [];
  @Input() defaultCourseId?: string;
  @Input() allowRoleSelection = false;
  @Input() defaultRole = 'STUDENT';
  @Input() modalTitle?: string;

  @Output() close = new EventEmitter<void>();
  @Output() invited = new EventEmitter<void>();

  mode: 'EMAIL' | 'MOBILE' = 'EMAIL';
  singleRecipient = '';
  bulkRecipients = '';
  isBulk = false;
  studentName = '';
  selectedRole = 'STUDENT';
  targetType: 'NONE' | 'LIVE_CLASS' | 'COURSE' = 'NONE';
  selectedLiveClassId = '';
  selectedCourseId = '';
  customMessage = '';

  isSending = signal(false);

  ngOnInit(): void {
    this.initTargetDefaults();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultLiveClassId'] || changes['defaultCourseId'] || changes['isOpen']) {
      this.initTargetDefaults();
    }
  }

  private initTargetDefaults(): void {
    if (this.defaultCourseId) {
      this.targetType = 'COURSE';
      this.selectedCourseId = this.defaultCourseId;
    } else if (this.defaultLiveClassId) {
      this.targetType = 'LIVE_CLASS';
      this.selectedLiveClassId = this.defaultLiveClassId;
    } else if (this.availableLiveClasses.length > 0) {
      this.targetType = 'LIVE_CLASS';
      this.selectedLiveClassId = this.availableLiveClasses[0]?.id || (this.availableLiveClasses[0] as any)?._id || '';
    } else if (this.availableCourses.length > 0) {
      this.targetType = 'COURSE';
      this.selectedCourseId = this.availableCourses[0]?._id || '';
    } else {
      this.targetType = 'NONE';
    }

    if (this.defaultRole) {
      this.selectedRole = this.defaultRole;
    }
  }

  setMode(mode: 'EMAIL' | 'MOBILE'): void {
    this.mode = mode;
  }

  setRole(role: string): void {
    this.selectedRole = role;
  }

  toggleBulk(): void {
    this.isBulk = !this.isBulk;
  }

  onClose(): void {
    this.resetForm();
    this.close.emit();
  }

  resetForm(): void {
    this.singleRecipient = '';
    this.bulkRecipients = '';
    this.studentName = '';
    this.customMessage = '';
    this.isBulk = false;
    this.initTargetDefaults();
  }

  sendInvitation(): void {
    const rawInput = this.isBulk ? this.bulkRecipients : this.singleRecipient;
    if (!rawInput || !rawInput.trim()) {
      this.toast.showToast(
        this.mode === 'EMAIL'
          ? 'Please enter at least one email address.'
          : 'Please enter at least one mobile phone number.',
        'error',
      );
      return;
    }

    const payload: CreateInvitationPayload = {
      recipients: rawInput.trim(),
      studentName: !this.isBulk && this.studentName ? this.studentName.trim() : undefined,
      role: this.allowRoleSelection ? this.selectedRole : (this.defaultRole || 'STUDENT'),
      liveClassId:
        this.targetType === 'LIVE_CLASS' && this.selectedLiveClassId
          ? this.selectedLiveClassId
          : undefined,
      courseId:
        this.targetType === 'COURSE' && this.selectedCourseId
          ? this.selectedCourseId
          : undefined,
      customMessage: this.customMessage ? this.customMessage.trim() : undefined,
    };

    this.isSending.set(true);
    this.invitationService.createInvitations(payload).subscribe({
      next: (res) => {
        this.isSending.set(false);
        if (res.errors && res.errors.length > 0) {
          const errorMsg = res.errors.map((e) => `${e.recipient}: ${e.error}`).join('; ');
          this.toast.showToast(`Sent ${res.createdCount} invite(s), but some failed: ${errorMsg}`, 'warning');
        } else {
          this.toast.showToast(
            res.createdCount > 1
              ? `Successfully sent ${res.createdCount} invitations!`
              : `Invitation sent successfully to ${rawInput.trim()}!`,
            'success',
          );
        }
        this.resetForm();
        this.invited.emit();
        this.close.emit();
      },
      error: (err) => {
        this.isSending.set(false);
        this.toast.showToast(err?.error?.message || 'Failed to send invitation.', 'error');
      },
    });
  }
}
